import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  FileInput,
  Input,
  Textarea,
} from "@/components";
import { axiosInstance } from "@/helpers/axios";
import styles from "./CreateSubsceneDrawer.module.scss";

type StructureOption = {
  id: string;
  structure_key: string;
  english_name: string;
  latin_name?: string | null;
  polish_name?: string | null;
  collection: string | null;
};

const normalizeSearch = (value: string) => value.trim().toLowerCase();

const structureMatchesSearch = (structure: StructureOption, query: string) => {
  if (!query) return true;
  const haystack = [
    structure.english_name,
    structure.structure_key,
    structure.collection,
    structure.latin_name,
    structure.polish_name,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(query);
};

type CreateSubsceneDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void | Promise<void>;
  parentVersionId: string;
  /** When set, creates a new version of an existing subscene. */
  existingSubscene?: {
    subsceneId: string;
    subsceneKey: string;
    displayName: string;
    structureKeys: string[];
  } | null;
};

export const CreateSubsceneDrawer = ({
  isOpen,
  onClose,
  onCreated,
  parentVersionId,
  existingSubscene,
}: CreateSubsceneDrawerProps) => {
  const [structures, setStructures] = useState<StructureOption[]>([]);
  const [structuresLoading, setStructuresLoading] = useState(false);
  const [subsceneKey, setSubsceneKey] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [changeDescription, setChangeDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [structureSearch, setStructureSearch] = useState("");

  const isNewVersion = Boolean(existingSubscene);
  const existingSubsceneId = existingSubscene?.subsceneId ?? null;

  useEffect(() => {
    if (!isOpen) return;
    setFile(null);
    setChangeDescription("");
    setFormError(null);
    setSuccess(false);
    setSubmitting(false);
    setStructureSearch("");

    if (existingSubscene) {
      setSubsceneKey(existingSubscene.subsceneKey);
      setDisplayName(existingSubscene.displayName);
      setSelectedKeys(existingSubscene.structureKeys);
    } else {
      setSubsceneKey("");
      setDisplayName("");
      setSelectedKeys([]);
    }

    setStructuresLoading(true);
    void (async () => {
      try {
        const { data } = await axiosInstance.get(
          `/v2/models/versions/${encodeURIComponent(parentVersionId)}/subscenes/structures`
        );
        setStructures((data?.data?.items ?? []) as StructureOption[]);
      } catch {
        setStructures([]);
      } finally {
        setStructuresLoading(false);
      }
    })();
  }, [isOpen, parentVersionId, existingSubsceneId, existingSubscene]);

  const searchQuery = normalizeSearch(structureSearch);

  const filteredStructures = useMemo(
    () => structures.filter((s) => structureMatchesSearch(s, searchQuery)),
    [structures, searchQuery],
  );

  const groupedStructures = useMemo(() => {
    const map = new Map<string, StructureOption[]>();
    for (const s of filteredStructures) {
      const col = s.collection ?? "other";
      const list = map.get(col) ?? [];
      list.push(s);
      map.set(col, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [filteredStructures]);

  const toggleKey = (key: string) => {
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!file) {
      setFormError("Choose a subscene model file.");
      return;
    }
    if (selectedKeys.length === 0) {
      setFormError("Select at least one structure to link this subscene.");
      return;
    }
    if (!isNewVersion && (!subsceneKey.trim() || !displayName.trim())) {
      setFormError("Subscene key and display name are required.");
      return;
    }

    setSubmitting(true);
    setFormError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("structureKeys", JSON.stringify(selectedKeys));
      fd.append("availableSexes", JSON.stringify(["female", "male"]));
      if (changeDescription.trim()) fd.append("changeDescription", changeDescription.trim());

      if (isNewVersion && existingSubscene) {
        fd.append("parentVersionId", parentVersionId);
        await axiosInstance.post(
          `/v2/models/subscenes/${encodeURIComponent(existingSubscene.subsceneId)}/versions`,
          fd
        );
      } else {
        fd.append("subsceneKey", subsceneKey.trim());
        fd.append("displayName", displayName.trim());
        await axiosInstance.post(
          `/v2/models/versions/${encodeURIComponent(parentVersionId)}/subscenes`,
          fd
        );
      }

      setSuccess(true);
      await onCreated();
    } catch (err: unknown) {
      const msg =
        err &&
        typeof err === "object" &&
        "response" in err &&
        err.response &&
        typeof err.response === "object" &&
        "data" in err.response &&
        err.response.data &&
        typeof err.response.data === "object" &&
        "message" in err.response.data
          ? String((err.response.data as { message?: string }).message)
          : "Upload failed.";
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      ariaLabel={isNewVersion ? "New subscene version" : "Add subscene"}
      stacked
    >
      <DrawerHeader
        title={
          success
            ? "Subscene queued"
            : isNewVersion
              ? `New version · ${existingSubscene?.displayName ?? "subscene"}`
              : "Add detail subscene"
        }
        onClose={handleClose}
        closeDisabled={submitting}
      />
      {success ? (
        <div className={styles["comp__stack"]}>
          <DrawerBody>
            <div className={styles["comp__success"]}>
              <strong>Subscene is queued for processing.</strong>
              <p>
                After chunking completes, the subscene is activated automatically and the main
                model version is bumped.
              </p>
            </div>
          </DrawerBody>
          <DrawerFooter>
            <Button type="button" variant="primary" onClick={handleClose}>
              Done
            </Button>
          </DrawerFooter>
        </div>
      ) : (
        <form className={styles["comp__stack"]} onSubmit={handleSubmit}>
          <DrawerBody>
            <div className={styles["comp"]}>
              <p className={styles["comp__hint"]}>
                Detail subscenes load instead of the main model when the user opens a linked
                structure. Uploading a subscene automatically bumps the main model patch version
                (e.g. v0.0.1 → v0.0.2). The main model must be published to the catalog for
                subscenes to appear in the app.
              </p>

              {!isNewVersion ? (
                <div className={styles["comp__row"]}>
                  <Input
                    id="subscene-key"
                    label="Subscene key"
                    value={subsceneKey}
                    placeholder="e.g. stomach-detail"
                    disabled={submitting}
                    onChange={setSubsceneKey}
                  />
                  <Input
                    id="subscene-display-name"
                    label="Display name"
                    value={displayName}
                    placeholder="e.g. Stomach (detailed)"
                    disabled={submitting}
                    onChange={setDisplayName}
                  />
                </div>
              ) : null}

              <div className={styles["comp__structures"]}>
                <div className={styles["comp__structures-header"]}>
                  <span className={styles["comp__structures-label"]}>
                    Linked structures
                  </span>
                  {structures.length > 0 ? (
                    <span className={styles["comp__structures-meta"]}>
                      {selectedKeys.length} selected
                      {searchQuery
                        ? ` · ${filteredStructures.length} of ${structures.length} shown`
                        : ` · ${structures.length} total`}
                    </span>
                  ) : null}
                </div>
                {structuresLoading ? (
                  <p className={styles["comp__structures-empty"]}>Loading structures…</p>
                ) : structures.length === 0 ? (
                  <p className={styles["comp__structures-empty"]}>
                    No structures yet — wait until the main model finishes processing.
                  </p>
                ) : (
                  <>
                    <Input
                      id="subscene-structure-search"
                      value={structureSearch}
                      placeholder="Search structures…"
                      disabled={submitting}
                      onChange={setStructureSearch}
                    />
                    <div className={styles["comp__structures-list"]}>
                      {groupedStructures.length === 0 ? (
                        <p className={styles["comp__structures-empty"]}>
                          No structures match &ldquo;{structureSearch.trim()}&rdquo;.
                        </p>
                      ) : (
                        groupedStructures.map(([collection, items]) => (
                          <div key={collection} className={styles["comp__structures-group"]}>
                            <div className={styles["comp__structures-group-title"]}>
                              {collection}
                            </div>
                            {items.map((s) => (
                              <Checkbox
                                key={s.structure_key}
                                id={`struct-${s.structure_key}`}
                                checked={selectedKeys.includes(s.structure_key)}
                                disabled={submitting}
                                onChange={() => toggleKey(s.structure_key)}
                              >
                                {s.english_name}{" "}
                                <span className={styles["comp__structure-key"]}>
                                  ({s.structure_key})
                                </span>
                              </Checkbox>
                            ))}
                          </div>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>

              <FileInput
                id="subscene-file"
                label="Subscene model file (.glb)"
                value={file}
                onChange={setFile}
                disabled={submitting}
                variant="secondary"
                fullWidth
              />

              <Textarea
                id="subscene-description"
                label="What changed (optional)"
                value={changeDescription}
                disabled={submitting}
                onChange={setChangeDescription}
                variant="secondary"
                size="medium"
              />

              {formError ? <div className={styles["comp__error"]}>{formError}</div> : null}
            </div>
          </DrawerBody>
          <DrawerFooter>
            <Button type="button" variant="secondary" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={submitting || structures.length === 0}>
              {submitting ? "Uploading…" : isNewVersion ? "Create version" : "Add subscene"}
            </Button>
          </DrawerFooter>
        </form>
      )}
    </Drawer>
  );
};
